// frontend/src/features/livestock/InboxPhotoGrid.tsx
// The photo inbox as a thumbnail grid (plan 4.3): oldest first, under month
// headings like the camera roll, each tile with its date and a "check date"
// badge when the date came from the file or the upload rather than the photo.

import { memo, useMemo } from 'react';
import {
  AspectRatio,
  Box,
  Center,
  SimpleGrid,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconPhoto } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { PhotoInboxItem } from '@/types/photoInbox';
import { CheckDateBadge } from './CheckDateBadge';
import { groupByMonth } from './monthGroups';
import { formatTakenAt, formatTileDate, isGuessedDate } from './photoInboxFormat';
import classes from './InboxPhotoGrid.module.css';

export interface InboxPhotoGridProps {
  photos: PhotoInboxItem[];
  /** A thumbnail failed to load (its signed URL may have expired). */
  onImageError: () => void;
}

/** Router state of a photo opened from the grid: going back returns to it. */
export interface InboxLocationState {
  fromInbox?: boolean;
}

const TILE_STATE: InboxLocationState = { fromInbox: true };

/** One photo; memoised, so a page of 60 more doesn't redraw the tiles already shown. */
const PhotoTile = memo(function PhotoTile({
  photo,
  onImageError,
}: {
  photo: PhotoInboxItem;
  onImageError: () => void;
}): JSX.Element {
  const guessed = isGuessedDate(photo.taken_at_source);
  return (
    <UnstyledButton
      component={Link}
      to={`/livestock/photos/${photo.id}`}
      state={TILE_STATE}
      className={classes.tile}
      aria-label={`Photo taken ${formatTakenAt(photo.taken_at)}${guessed ? ', check date' : ''}`}
    >
      <AspectRatio ratio={1}>
        {photo.thumb_url ? (
          <img
            src={photo.thumb_url}
            alt=""
            loading="lazy"
            decoding="async"
            className={classes.thumb}
            onError={onImageError}
          />
        ) : (
          <Center className={classes.thumb}>
            <IconPhoto size={16} color="var(--mantine-color-gray-7)" />
          </Center>
        )}
      </AspectRatio>
      <Text size="xs" mt={4} lh={1.3} className={classes.date}>
        {formatTileDate(photo.taken_at)}
      </Text>
      {guessed && (
        <Box mt={2}>
          <CheckDateBadge />
        </Box>
      )}
    </UnstyledButton>
  );
});

const takenAt = (photo: PhotoInboxItem): string => photo.taken_at;

export function InboxPhotoGrid({ photos, onImageError }: InboxPhotoGridProps): JSX.Element {
  const groups = useMemo(() => groupByMonth(photos, takenAt), [photos]);
  return (
    <Stack gap="lg">
      {groups.map((group) => (
        <section key={group.key} aria-label={group.label}>
          <Title
            order={3}
            fz={{ base: 16, sm: 18 }}
            fw={600}
            mb="xs"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {group.label}
          </Title>
          <SimpleGrid
            cols={{ base: 3, xs: 4, sm: 5, md: 6, lg: 8 }}
            spacing={{ base: 8, sm: 'sm' }}
          >
            {group.items.map((photo) => (
              <PhotoTile key={photo.id} photo={photo} onImageError={onImageError} />
            ))}
          </SimpleGrid>
        </section>
      ))}
    </Stack>
  );
}
